/* Brand identity for the form bundle — a thin view over the repo-root
   brand.config.js (the file to edit when re-branding the platform).

   On a multi-brand deployment the server injects the tenant's identity into
   the document as an inert JSON block BEFORE this module runs, and it wins. The
   built-in config stays the base, so a tenant that overrides only a colour
   still gets every other value from the shipped brand. The embeddable widget
   is mounted in someone else's page, where no such global exists, and simply
   uses the built-in config. */
import CONFIG from "../brand.config.js";

import { RUNTIME_BRAND as RUNTIME } from "./runtimeBrand.js";

/* Keys that are a BRAND'S OWN. Spreading the tenant over the built-in config
   filled these in from the platform whenever a brand had not set them, so a
   brand with no phone number of its own showed OURS on its own form, and one
   with no legal entity showed our licensee. An absent value is absent - every
   surface here already renders without one - and a wrong one is a false
   statement about who the applicant is dealing with. Structural keys (theme,
   lender wording) still inherit: there is no identity in them, and a brand
   that sets only a colour must keep working. */
const NEVER_INHERITED = ["tagline", "website", "phone", "emails", "address", "legal", "logoUrl", "mascotUrl", "faviconUrl", "referencePrefix"];

function forTenant(runtime) {
  const out = { ...CONFIG, ...runtime };
  for (const k of NEVER_INHERITED) if (runtime[k] === undefined) delete out[k];
  return out;
}

export const BRAND = RUNTIME ? forTenant(RUNTIME) : CONFIG;

/* Legal footer line for the entity that writes the given product.
   Commercial / asset finance is often a different entity than NCCP consumer
   lending — never show a consumer credit licence on a commercial product. */
export function entityLine(product) {
  const legal = BRAND.legal || {};
  const { consumer, commercial } = legal;
  if (product === "commercial" && commercial) {
    return `${commercial.entity} · ABN ${commercial.abn}`;
  }
  if (consumer) return `${consumer.entity} · ACL ${consumer.acl}`;
  // A brand onboarded without the consumer/commercial split named (which is a
  // decision only that brand can make) must not crash the form. Fall back to
  // the first entity it did give us, and to the trading name if it gave none.
  const first = Array.isArray(legal.entities) ? legal.entities[0] : null;
  if (first) return first.acl ? `${first.entity} · ACL ${first.acl}` : `${first.entity} · ABN ${first.abn}`;
  return BRAND.name;
}

/* The entity to NAME in a consent tick or a footer, for brands that have not
   declared the consumer/commercial split. Crashing the form because a brand
   was onboarded without that split is the worst of the options: the split is a
   decision only that brand can make, and until they make it the trading name
   is the honest thing to show. Onboarding records the gap (see the seed file's
   _unconfirmed block) so it is chased rather than forgotten. */
export function legalEntityName(kind = "consumer") {
  const legal = BRAND.legal || {};
  if (legal[kind]?.entity) return legal[kind].entity;
  const entities = Array.isArray(legal.entities) ? legal.entities : [];
  const licensed = entities.find((e) => e.acl) || entities[0];
  return licensed?.entity || BRAND.name;
}
